import { LARegion } from "@dana/Common/Constants";
import { MakePicker } from "@dana/Components/ProductList/MakePicker";
import { ModelPicker } from "@dana/Components/ProductList/ModelPicker";
import { YearPicker } from "@dana/Components/ProductList/YearPicker";
import DanaApplicationState from "@dana/Store/DanaApplicationState";
import getVehicleFilters from "@dana/Store/Data/SearchVehicleFilter/Handlers/SearchVehicleHandler";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getLanguages, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, AttributeValueFacetModel, LanguageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isAccordian?: boolean;
    onProductSearch?: () => void
}

const mapStateToProps = (state: DanaApplicationState) => {
    const {
        searchSettings: { searchPath },
    } = getSettingsCollection(state);
    const searchVehicleFiltersSate = state.searchVehicleFilter || {};

    return {
        searchVehicleFiltersSate,
        searchPath,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
    };
}

const mapDispatchToProps = {
    getVehicleFilters,
    setNavDrawerIsOpen,
    addProductFilters
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;


export interface GlobalSearchByVehicleStyles {
    mainContainer?: InjectableCss;
    container?: GridContainerProps;
    gridItems?: GridItemProps;
    filterButton?: ButtonPresentationProps;
    searchByVehicleText?: TypographyPresentationProps;
}

export const globalSearchByVehicleStyles: GlobalSearchByVehicleStyles = {
    container: {
        offsetCss: css`
           
            > ${GridItemStyle} {
                &:not(:last-child) {
                    margin-bottom: 16px;
                }
            }
        `,
        css: css`
            padding: 16px;
        `,
    },
    searchByVehicleText: {
        variant: "h3",

        css: css`
            margin-bottom: 24px;
        `
    },
    gridItems: {
        css: css`
            padding: 0;
        `,
    },
    filterButton: {
        css: css`
            width: -webkit-fill-available;
        `
    }
};

const styles = globalSearchByVehicleStyles;
const LASort = ["Model", "Year", "Make"];
const NASort = ["Year", "Make", "Model"];
const GlobalSearchByVehicleComponant: React.FC<Props> = ({ setNavDrawerIsOpen, currentLanguage, languages, history, searchPath, searchVehicleFiltersSate, getVehicleFilters }) => {

    const [years, setYears] = useState<AttributeTypeFacetModel | undefined>(undefined);
    const [makes, setMakes] = useState<AttributeTypeFacetModel | undefined>(undefined);
    const [models, setModels] = useState<AttributeTypeFacetModel | undefined>(undefined);
    const [selectedYear, setSelectedYear] = useState<AttributeValueFacetModel | undefined>(undefined);
    const [selectedMake, setSelectedMake] = useState<AttributeValueFacetModel | undefined>(undefined);
    const [selectedModel, setSelectedModel] = useState<AttributeValueFacetModel | undefined>(undefined);
    const [year, setYear] = useState<string>("");
    const [make, setMake] = useState<string>("");
    const [model, setModel] = useState<string>("");
    const [dropDownSortOrder, setDropdownDownSortOrder] = useState<string[]>([]);

    useEffect(() => {
        setYears(searchVehicleFiltersSate?.yearFacet);
        setMakes(searchVehicleFiltersSate?.makeFacet);
        setModels(searchVehicleFiltersSate?.modelFacet);
    }, [searchVehicleFiltersSate]);

    useEffect(() => {
        const selectedLanguage = languages?.find((l: LanguageModel) => l.id === currentLanguage?.id);
        if (selectedLanguage?.properties?.region === LARegion) {
            setDropdownDownSortOrder(LASort)
        } else {
            setDropdownDownSortOrder(NASort);
        }

    }, [currentLanguage]);

    useEffect(() => {
        if (dropDownSortOrder.length > 0) {
            getVehicleFilters({
                year,
                make,
                model,
                sortOrder: dropDownSortOrder.join("|")
            });
        }
    }, [year, model, make, dropDownSortOrder]);
    const setYearHandler = (yearValue: string) => {
        handleDropdownChange("Year");
        const selectedYearFacet = years?.attributeValueFacets?.find(x => x.attributeValueId === yearValue);
        if (selectedYearFacet) {
            setSelectedYear(selectedYearFacet);
            setYear(selectedYearFacet?.value);
        } else {
            setSelectedYear({} as AttributeValueFacetModel);
            setYear("");
        }
    };

    const setMakeHandler = (makeValue: string) => {
        handleDropdownChange("Make");
        const selectedMakeFacet = makes?.attributeValueFacets?.find(x => x.attributeValueId === makeValue);
        if (selectedMakeFacet) {
            setSelectedMake(selectedMakeFacet);
            setMake(selectedMakeFacet?.value);
        } else {
            setSelectedMake({} as AttributeValueFacetModel);
            setMake("");
        }
    };

    const setModelHandler = (modelValue: string) => {
        handleDropdownChange("Model");
        const selectedModelFacet = models?.attributeValueFacets?.find(x => x.attributeValueId === modelValue);
        if (selectedModelFacet) {
            setSelectedModel(selectedModelFacet);
            setModel(selectedModelFacet?.value)
        } else {
            setSelectedModel({} as AttributeValueFacetModel);
            setModel("");
        }
    };
    const handleDropdownChange = (key: string) => {

        const index = dropDownSortOrder.indexOf(key);
        dropDownSortOrder.slice(index + 1).forEach(k => {

            if (k === "Year") {
                setYears({} as AttributeTypeFacetModel);
                setSelectedYear({} as AttributeValueFacetModel);
                setYear("");
            }
            if (k === "Make") {
                setMakes({} as AttributeTypeFacetModel);
                setSelectedMake({} as AttributeValueFacetModel);
                setMake("");
            }
            if (k === "Model") {
                setModels({} as AttributeTypeFacetModel);
                setSelectedModel({} as AttributeValueFacetModel);
                setModel("");
            }
        });

    };

    const searchParts = () => {
        const attributeIds = [
            selectedYear?.attributeValueId,
            selectedMake?.attributeValueId,
            selectedModel?.attributeValueId,
        ].filter(Boolean);

        if (attributeIds.length > 0) {

            const params = new URLSearchParams();
           
            if (attributeIds.length > 0) {
                params.append("attributeValueIds", attributeIds.join(","));
            }

            const finalUrl = `/${searchPath}?${params.toString()}`;

            addProductFilters({
                attributeValueIds: attributeIds.filter((id): id is string => typeof id === "string"),
                brandIds:[]
            });
            history.push(finalUrl);
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
    }

    const pickerMap = {
        Year: {
            Component: YearPicker,
            facet: years,
            selected: selectedYear?.attributeValueId,
            onSelect: setYearHandler,
        },
        Make: {
            Component: MakePicker,
            facet: makes,
            selected: selectedMake?.attributeValueId,
            onSelect: setMakeHandler,
        },
        Model: {
            Component: ModelPicker,
            facet: models,
            selected: selectedModel?.attributeValueId,
            onSelect: setModelHandler,
        }
    };

    return (
        <>

            <GridContainer {...globalSearchByVehicleStyles.container} className="global-search-by-vahicle">
                {dropDownSortOrder.map((key, index) => {
                    const picker = pickerMap[key as keyof typeof pickerMap];
                    if (!picker) {
                        return null;
                    }

                    const facet = picker.facet;
                    const selected = picker.selected;
                    const onSelect = picker.onSelect;
                    let isenabled = true;
                    if (index > 0) {
                        const previousKey = dropDownSortOrder[index - 1];
                        const previousPicker = pickerMap[previousKey as keyof typeof pickerMap];
                        isenabled = !!previousPicker?.selected;
                    }
                    return (
                        <GridItem {...globalSearchByVehicleStyles.gridItems} width={12} key={facet?.attributeTypeId + key}>

                            {
                                key === "Year" && (
                                    <YearPicker
                                        isEnabled={isenabled}
                                        yearFacet={facet}
                                        selectedYear={selected}
                                        onYearSelect={onSelect}
                                    />
                                )
                            }
                            {
                                key === "Make" && (
                                    <MakePicker
                                        isEnabled={isenabled}
                                        makeFacet={facet}
                                        selectedMake={selected}
                                        onMakeSelect={onSelect}
                                    />
                                )
                            }
                            {
                                key === "Model" && (
                                    <ModelPicker
                                        isEnabled={isenabled}
                                        modelFacet={facet}
                                        selectedModel={selected}
                                        onModelSelect={onSelect}
                                    />
                                )
                            }
                        </GridItem>
                    );
                })}

                <GridItem {...globalSearchByVehicleStyles.gridItems} width={12}>
                    <Button
                        disabled={!year || !make || !model}
                        {...globalSearchByVehicleStyles.filterButton}
                        title="Go"
                        onClick={searchParts}
                    >
                        {translate("Go")}
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    );
};



export default connect(mapStateToProps, mapDispatchToProps)(withHistory(GlobalSearchByVehicleComponant));
